/**
 * Internal-only: bundle the Cohere fonts into a .zip laid out to match the
 * file paths in exported co/plot Python scripts, so an MLE can unzip it next to
 * a script and have it run without "cannot find font" errors.
 *
 * The fonts are served as static assets only on the internal deployment
 * (see scripts/prepare-fonts.js), so this must stay gated by
 * deploymentProfile.isInternal at the call site.
 */

import { createZip } from './zip';

const PUBLIC = process.env.PUBLIC_URL || '';

// [served source path, path inside the zip]. The zip paths intentionally mirror
// the relative paths emitted by codeSnippets.js / seabornSnippets.js.
const FONT_FILES = [
  ['/fonts/internal/CohereText-Regular.ttf', 'fonts/CohereText-Regular.ttf'],
  ['/fonts/internal/Unica77Cohere-Regular.otf', 'fonts/internal/Unica77Cohere-Regular.otf'],
];

const README = `co/plot font assets (internal use only)
=======================================

These are the Cohere fonts used by co/plot, laid out to match the paths in
exported co/plot Python scripts.

Quick start
-----------
1. Unzip this archive in the SAME folder as your exported co/plot script.
   Your script loads:
     fonts/CohereText-Regular.ttf              (numbers)
     fonts/internal/Unica77Cohere-Regular.otf  (labels & titles)
2. Run the script as usual; matplotlib will find the fonts.

Writing your own matplotlib / seaborn code?
-------------------------------------------
- Run:  python register_fonts.py
  then use family names "Cohere Text" (numbers) and "Unica77 Cohere" (labels).
- Or, simpler, install the theme package and let it handle fonts + styling:
    pip install "git+ssh://git@github.com/cohere-ai/coplot-theme.git"
    import coplot_theme as ct; ct.use_theme(); ...; ct.finalize(fig)

These fonts are proprietary and for internal Cohere use only. Do not redistribute.
`;

const REGISTER_PY = `"""Register the bundled Cohere fonts with matplotlib.

Run once per process:  python register_fonts.py  (or import register_fonts).
"""
from pathlib import Path

import matplotlib.font_manager as fm

_FONT_DIR = Path(__file__).parent / "fonts"


def register_fonts():
    for pattern in ("*.ttf", "*.otf"):
        for font_file in _FONT_DIR.rglob(pattern):
            fm.fontManager.addfont(str(font_file))


if __name__ == "__main__":
    register_fonts()
    print("Registered Cohere fonts: 'Cohere Text' (numbers), 'Unica77 Cohere' (labels).")
`;

async function fetchBytes(path) {
  const res = await fetch(`${PUBLIC}${path}`);
  if (!res.ok) throw new Error(`Failed to fetch ${path} (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Build and download coplot-fonts.zip. Throws if any font asset is missing.
 */
export async function downloadFontAssets(filename = 'coplot-fonts.zip') {
  const entries = [];
  for (const [src, dest] of FONT_FILES) {
    entries.push({ name: dest, data: await fetchBytes(src) });
  }
  entries.push({ name: 'README.txt', data: README });
  entries.push({ name: 'register_fonts.py', data: REGISTER_PY });

  triggerDownload(createZip(entries), filename);
}
